import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import siteConfig from '../config/site.json';

const Hero: React.FC = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const phoneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Animate hero content
    if (heroRef.current) {
      const tl = gsap.timeline();
      
      tl.fromTo(
        heroRef.current.querySelector('.hero-title'),
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration: 1, ease: 'power2.out' }
      )
      .fromTo(
        heroRef.current.querySelector('.hero-subtitle'),
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out', delay: '-=0.5' }
      )
      .fromTo(
        heroRef.current.querySelector('.hero-cta'),
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out', delay: '-=0.3' }
      );
    }

    // Animate phone mockup
    if (phoneRef.current) {
      gsap.fromTo(
        phoneRef.current,
        { opacity: 0, x: 100, rotationY: -45 },
        { opacity: 1, x: 0, rotationY: -8, duration: 1.2, ease: 'power2.out', delay: 0.5 }
      );
    }

    // Animate floating elements
    gsap.fromTo(
      '.floating-icon',
      { opacity: 0, scale: 0 },
      { opacity: 1, scale: 1, duration: 0.6, stagger: 0.2, ease: 'back.out(1.7)', delay: 1 }
    );

    // Animate particles
    gsap.fromTo(
      '.particle',
      { opacity: 0, scale: 0 },
      { opacity: 1, scale: 1, duration: 0.4, stagger: 0.1, ease: 'power2.out', delay: 1.2 }
    );

    // Animate morphing shapes
    gsap.fromTo(
      '.morph-shape',
      { opacity: 0, scale: 0, rotation: 180 },
      { opacity: 1, scale: 1, rotation: 0, duration: 1, stagger: 0.3, ease: 'back.out(1.7)', delay: 1.4 }
    );

    // Floating elements parallax effect
    gsap.to('.floating-icon', {
      y: -50,
      duration: 3,
      ease: 'power1.inOut',
      stagger: 1,
      repeat: -1,
      yoyo: true
    });

    // Particle system animation
    gsap.to('.particle', {
      y: -window.innerHeight,
      duration: 8,
      ease: 'none',
      repeat: -1,
      stagger: 0.5
    });

    // Morphing shapes animation
    gsap.to('.morph-shape', {
      rotation: 360,
      scale: 1.2,
      duration: 10,
      ease: 'power1.inOut',
      repeat: -1,
      yoyo: true,
      stagger: 2
    });

    // Animate stats with counter effect
    if (statsRef.current) {
      const stats = statsRef.current.querySelectorAll('.interactive-stat');
      
      stats.forEach((stat, index) => {
        const numberElement = stat.querySelector('.stat-number');
        const target = parseInt(stat.getAttribute('data-target') || '0');
        const suffix = stat.getAttribute('data-suffix') || '';
        
        // Animate the stat container first
        gsap.fromTo(
          stat,
          { opacity: 0, y: 20 },
          { 
            opacity: 1, 
            y: 0, 
            duration: 0.6, 
            delay: 0.8 + (index * 0.2),
            ease: 'power2.out' 
          }
        );
        
        // Then animate the counter
        gsap.fromTo(
          numberElement,
          { innerHTML: 0 },
          {
            innerHTML: target,
            duration: 2,
            delay: 1.2 + (index * 0.2),
            ease: 'power2.out',
            onUpdate: function() {
              const current = Math.floor(this.targets()[0].innerHTML);
              if (numberElement) {
                numberElement.innerHTML = current + suffix;
              }
            }
          }
        );
      });
    }
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const { hero } = siteConfig;
  const whatsappNumber = siteConfig.site.contact.whatsapp.replace(/[^0-9]/g, '');
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<'english' | 'hindi' | 'marathi' | null>(null);

  const videoSources = {
    english: '/English_shopzo_dashbopard.mp4',
    hindi: '/Hindi_shopzo_dashboard.mp4',
    marathi: '/Marathi_shopzo_dashboard.mp4',
  };

  const openDemoModal = () => {
    setShowDemoModal(true);
    setSelectedLanguage(null);
  };
  const closeDemoModal = () => {
    setShowDemoModal(false);
    setSelectedLanguage(null);
  };

  return (
    <section className="hero" ref={heroRef}>
      <div className="hero-container">
        <div className="hero-content">
          <h1 className="hero-title">
            Connect to your customers via <span className="gradient-text">WhatsApp</span>, without changing your software
          </h1>
          {/* Centered horizontal button group */}
          <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '1.5rem', margin: '2rem 0' }}>
            <a
              href={`https://wa.me/${whatsappNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              className="primary-button"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <i className="fab fa-whatsapp"></i>
              Start Free Trial
            </a>
            <button
              className="secondary-button"
              onClick={openDemoModal}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
            >
              <i className="fas fa-play"></i>
              Watch Demo
            </button>
          </div>
          {/* Demo Modal */}
          {showDemoModal && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div style={{
                background: '#fff',
                borderRadius: '16px',
                padding: '2rem',
                maxWidth: '90vw',
                maxHeight: '90vh',
                boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
                position: 'relative',
                minWidth: '320px',
                minHeight: selectedLanguage ? 'auto' : '200px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <button
                  onClick={closeDemoModal}
                  style={{
                    position: 'absolute',
                    top: '1rem',
                    right: '1rem',
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#888'
                  }}
                  aria-label="Close"
                >
                  &times;
                </button>
                {!selectedLanguage ? (
                  <>
                    <h3 style={{ marginBottom: '1.5rem' }}>Select Language</h3>
                    <div style={{ display: 'flex', gap: '1rem' }}>
                      <button
                        className="primary-button"
                        onClick={() => setSelectedLanguage('english')}
                      >
                        English
                      </button>
                      <button
                        className="primary-button"
                        onClick={() => setSelectedLanguage('hindi')}
                      >
                        Hindi
                      </button>
                      <button
                        className="primary-button"
                        onClick={() => setSelectedLanguage('marathi')}
                      >
                        Marathi
                      </button>
                    </div>
                  </>
                ) : (
                  selectedLanguage && (
                    <div style={{ width: '100%', maxWidth: '600px' }}>
                      <video
                        key={selectedLanguage}
                        src={videoSources[selectedLanguage]}
                        controls
                        autoPlay
                        style={{ width: '100%', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}
                      />
                    </div>
                  )
                )}
              </div>
            </div>
          )}
          <div className="hero-stats" ref={statsRef}>
            {hero.statistics.map((stat) => (
              <div 
                key={stat.id}
                className="stat interactive-stat" 
                data-target={stat.value}
                data-suffix={stat.suffix}
              >
                <span className="stat-number">0</span>
                <span className="stat-label">{stat.label}</span>
                <div className="stat-progress"></div>
              </div>
            ))}
          </div>
        </div>
        
        <div className="hero-visual">
          <div className="phone-mockup" ref={phoneRef}>
            <div className="phone-frame">
              <div className="phone-notch"></div>
              <div className="phone-speaker"></div>
              <div className="phone-camera"></div>
              <div className="phone-side-buttons">
                <div className="volume-up"></div>
                <div className="volume-down"></div>
                <div className="power-button"></div>
              </div>
              <div className="phone-screen">
                <div className="status-bar">
                  <div className="status-left">
                    <span className="time">9:41</span>
                  </div>
                  <div className="status-right">
                    <div className="signal-bars">
                      <span></span>
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                    <div className="wifi-icon">
                      <i className="fas fa-wifi"></i>
                    </div>
                    <div className="battery">
                      <div className="battery-level"></div>
                    </div>
                  </div>
                </div>
                <div className="whatsapp-chat">
                  <div className="chat-header">
                    <div className="header-left">
                      <i className="fas fa-arrow-left"></i>
                      <div className="chat-avatar">
                        <i className="fas fa-store"></i>
                      </div>
                      <div className="chat-info">
                        <h4>Spinneys Supermart</h4>
                        <span>online</span>
                      </div>
                    </div>
                    <div className="header-actions">
                      <i className="fas fa-video"></i>
                      <i className="fas fa-phone"></i>
                      <i className="fas fa-ellipsis-v"></i>
                    </div>
                  </div>
                  <div className="chat-messages">
                    <div className="message received">
                      <p>Hi! Here's your invoice for today's order.</p>
                      <div className="invoice-preview">
                        <div className="invoice-header">
                          <h5>INVOICE #SP2025-001</h5>
                        </div>
                        <div className="invoice-details">
                          <p>Grocery Order #12345</p>
                          <p>Date: 11/07/2025</p>
                          <p>Items: 12 products</p>
                        </div>
                        <div className="invoice-actions">
                          <button className="pay-button">Pay Now</button>
                          <button className="download-button">Download PDF</button>
                        </div>
                      </div>
                      <span className="time">2:31 PM</span>
                    </div>
                    <div className="message sent">
                      <p>Thanks! That's snappy! 😊</p>
                      <span className="time">2:32 PM</span>
                    </div>
                  </div>
                  <div className="chat-input">
                    <div className="input-container">
                      <input type="text" placeholder="Type a message" disabled />
                      <button className="send-button" disabled>
                        <i className="fas fa-paper-plane"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="home-indicator"></div>
            </div>
          </div>
          
          <div className="floating-elements">
            <div className="floating-icon" data-speed="2">
              <i className="fas fa-chart-line"></i>
            </div>
          </div>
          
          <div className="particles">
            <div className="particle"></div>
            <div className="particle"></div>
            <div className="particle"></div>
            <div className="particle"></div>
            <div className="particle"></div>
            <div className="particle"></div>
            <div className="particle"></div>
            <div className="particle"></div>
            <div className="particle"></div>
            <div className="particle"></div>
          </div>
          
          <div className="morphing-shapes">
            <div className="morph-shape"></div>
            <div className="morph-shape"></div>
          </div>
        </div>
      </div>
      <div className="hero-background">
        <div className="gradient-orb"></div>
        <div className="gradient-orb"></div>
      </div>
    </section>
  );
};

export default Hero; 